import { useHeaderPanels } from '@/components/content/Header/context/HeaderPanelsContext';
import { HeaderCommonPanel } from '@/components/content/Header/parts/HeaderCommonPanel';
import { LoginRegisterPanel } from '@/components/content/Header/parts/LoginRegisterPanel';
import { MyWishlistDropMenuPanel } from '@/components/content/Header/parts/MyWishlist/DropMenuPanel';
import {
	headerMyWishListMobileContainerSX,
	myWishListHeaderLabelSX,
	myWishListHeaderSX,
} from '@/components/content/Header/styles/myWishList';
import DynamicIcon from '@/components/icons/DynamicIcon';
import { useLocalization } from '@/data/Localization';
import { useUser } from '@/data/User';
import { Box, Stack, Typography, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { FC, useState } from 'react';
import { MATERIAL_SYMBOL_ICONS, OPEN_CLOSE_STATES } from 'shared/custom/constants/commonConstants';

export const MyWishListHeader: FC = () => {
	const idPopper = 'wishlistPanel';
	const { activePanelId, anchorEl, togglePanel, closePanel } = useHeaderPanels();
	const isPanelOpen = activePanelId === idPopper;
	const [isHovered, setIsHovered] = useState(false);
	const isActive = isPanelOpen || isHovered;

	const theme = useTheme();
	const HeaderLabels = useLocalization('Header');
	const panelLabels = useLocalization('SignInRegisterPanel');
	const { user } = useUser();
	const isMobile = useMediaQuery(theme.breakpoints.down('lg'));

	const handlePanelStatus = (
		event:
			| React.MouseEvent<HTMLElement>
			| React.KeyboardEvent<HTMLDivElement>
			| React.FocusEvent<HTMLDivElement, Element>
			| null,
		action?: OPEN_CLOSE_STATES
	) => {
		if (action === OPEN_CLOSE_STATES.OPEN && isPanelOpen) {
			closePanel();
			return;
		}
		if (activePanelId === null) togglePanel(idPopper, event?.currentTarget ?? null);
	};
	const handleOnKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
		if (event.key === 'Enter' || event.key === ' ') {
			event.preventDefault();
			handlePanelStatus(event, OPEN_CLOSE_STATES.OPEN);
		}
	};

	return (
		<Box>
			<Stack
				aria-describedby="my-wish-list-panel"
				aria-expanded={isPanelOpen}
				alignItems="center"
				sx={myWishListHeaderSX(theme, isPanelOpen)}
				onClick={(e) => handlePanelStatus(e, OPEN_CLOSE_STATES.OPEN)}
				onMouseEnter={() => setIsHovered(true)}
				onMouseLeave={() => setIsHovered(false)}
				tabIndex={0}
				onKeyDown={handleOnKeyDown}
			>
				<Box sx={headerMyWishListMobileContainerSX(theme)} id="wishlistPanelTrigger">
					<DynamicIcon
						name={MATERIAL_SYMBOL_ICONS.wishlist}
						variant={isActive ? 'filled' : 'rounded'}
					/>
					<Typography component="p" variant="bodySubText" sx={myWishListHeaderLabelSX()}>
						{HeaderLabels.wishList.t()}
					</Typography>
				</Box>
			</Stack>

			<HeaderCommonPanel
				idPopper={idPopper}
				isOpen={isPanelOpen}
				anchorEl={anchorEl}
				handleClose={closePanel}
			>
				<Stack direction="row">
					{user?.isLoggedIn ? (
						<MyWishlistDropMenuPanel setOpenPanel={() => togglePanel(idPopper, anchorEl)} />
					) : (
						<LoginRegisterPanel
							openPanel={isPanelOpen}
							setOpenPanel={() => togglePanel(idPopper, anchorEl)}
							icon={isMobile ? MATERIAL_SYMBOL_ICONS.wishlist : ''}
							title={panelLabels.Titles.MyWishlist.t()}
							panelText={panelLabels.PanelTexts.MyWishlist.t()}
							classProp="wishlist"
						/>
					)}
				</Stack>
			</HeaderCommonPanel>
		</Box>
	);
};
